import { useState, useMemo, useCallback, useEffect } from 'react';
import { WorkflowBlock, Connector, BlockGroup } from '@/types/workflow';
import { dispatchOnboardingEvent } from '@/components/onboarding/uiAdapter';
import { toast } from '@/hooks/use-toast';

/**
 * How many layers deep the user can drill before navigation is refused.
 * Beyond this, breadcrumbs become hard to read and the navigation UX
 * degrades. Mostly a sanity cap — most workflows live at 0–2 levels.
 */
const MAX_LAYER_DEPTH = 5;

export function useLayerNavigation(
  blocks: WorkflowBlock[],
  connectors: Connector[],
  groups: BlockGroup[]
) {
  const [layerPath, setLayerPath] = useState<string[]>([]);

  // Validate path — if any group in the path was deleted, truncate
  useEffect(() => {
    setLayerPath(prev => {
      for (let i = 0; i < prev.length; i++) {
        if (!groups.some(g => g.id === prev[i])) {
          return prev.slice(0, i);
        }
      }
      return prev;
    });
  }, [groups]);

  const currentGroupId = layerPath.length > 0 ? layerPath[layerPath.length - 1] : null;

  const navigateInto = useCallback((groupId: string) => {
    setLayerPath(prev => {
      if (prev.length >= MAX_LAYER_DEPTH) {
        toast({
          title: 'Maximalt djup nått',
          description: `Du kan inte gå djupare än ${MAX_LAYER_DEPTH} lager — workflow:t blir svårnavigerat.`,
          variant: 'destructive',
        });
        return prev;
      }
      return [...prev, groupId];
    });
    dispatchOnboardingEvent({ name: 'layer:entered', target: groupId });
  }, []);

  const navigateUp = useCallback(() => {
    setLayerPath(prev => prev.slice(0, -1));
  }, []);

  const navigateToLevel = useCallback((index: number) => {
    setLayerPath(prev => prev.slice(0, index));
  }, []);

  const resetLayerPath = useCallback(() => {
    setLayerPath([]);
  }, []);

  // Groups at the current layer level
  const currentLayerGroups = useMemo(
    () => groups.filter(g => (g.parentGroupId ?? null) === currentGroupId),
    [groups, currentGroupId]
  );

  // Collapsed sub-groups at current level (shown as cards)
  const collapsedGroups = useMemo(
    () => currentLayerGroups.filter(g => g.collapsed),
    [currentLayerGroups]
  );

  // Non-collapsed groups at current level (shown as frames)
  const expandedGroups = useMemo(
    () => currentLayerGroups.filter(g => !g.collapsed),
    [currentLayerGroups]
  );

  // Recursively collect all blockIds from a group and its descendant groups
  const getAllDescendantBlockIds = useCallback(
    (groupId: string): string[] => {
      const group = groups.find(g => g.id === groupId);
      if (!group) return [];
      const childGroups = groups.filter(g => g.parentGroupId === groupId);
      return [
        ...group.blockIds,
        ...childGroups.flatMap(cg => getAllDescendantBlockIds(cg.id)),
      ];
    },
    [groups]
  );

  // Block IDs hidden inside collapsed sub-groups (recursive)
  const hiddenBlockIds = useMemo(
    () => new Set(collapsedGroups.flatMap(g => getAllDescendantBlockIds(g.id))),
    [collapsedGroups, getAllDescendantBlockIds]
  );

  // Blocks visible at current layer (not hidden in collapsed groups)
  const visibleBlocks = useMemo(() => {
    if (currentGroupId) {
      const currentGroup = groups.find(g => g.id === currentGroupId);
      if (!currentGroup) return [];
      return blocks.filter(
        b => currentGroup.blockIds.includes(b.id) && !hiddenBlockIds.has(b.id)
      );
    } else {
      // Root level: all blocks not in collapsed top-level groups
      return blocks.filter(b => !hiddenBlockIds.has(b.id));
    }
  }, [blocks, groups, currentGroupId, hiddenBlockIds]);

  // Map any ID (block or group) → its visible representative
  const getVisibleRepresentative = useCallback(
    (id: string): { id: string; isGroup: boolean } | null => {
      // 1. It's a visible block
      if (visibleBlocks.some(b => b.id === id)) {
        return { id, isGroup: false };
      }
      // 2. It IS a collapsed group at this level
      if (collapsedGroups.some(g => g.id === id)) {
        return { id, isGroup: true };
      }
      // 3. It's a block inside a collapsed group (direct or nested)
      for (const group of collapsedGroups) {
        const allIds = getAllDescendantBlockIds(group.id);
        if (allIds.includes(id)) {
          return { id: group.id, isGroup: true };
        }
      }
      // 4. It's a nested group inside a collapsed group
      for (const group of collapsedGroups) {
        const _nestedGroups = groups.filter(g => g.parentGroupId === group.id);
        const isNestedGroup = (targetId: string, parentId: string): boolean => {
          const children = groups.filter(g => g.parentGroupId === parentId);
          for (const child of children) {
            if (child.id === targetId) return true;
            if (isNestedGroup(targetId, child.id)) return true;
          }
          return false;
        };
        if (isNestedGroup(id, group.id)) {
          return { id: group.id, isGroup: true };
        }
      }
      return null;
    },
    [visibleBlocks, collapsedGroups, getAllDescendantBlockIds, groups]
  );

  // Connectors remapped for current layer view.
  //
  // Tidigare deduplicerades resultatet på `${fromId}->${toId}` — ett
  // arv från när 1 connector / blockpar var lagen. Med
  // dual-directional flow + port-uniqueness kan A↔B ha flera linjer
  // (top→top, bottom→bottom, top-right→top-left osv), så vi
  // deduplicerar nu på connector-id istället för endpoint-paret.
  // Annars renderas bara den första matchande linjen och alla andra
  // försvinner visuellt även om de finns kvar i state.
  const visibleConnectors = useMemo(() => {
    const mapped: Connector[] = [];
    const seenIds = new Set<string>();

    for (const conn of connectors) {
      const fromRep = getVisibleRepresentative(conn.fromId);
      const toRep = getVisibleRepresentative(conn.toId);

      if (!fromRep || !toRep) continue;
      // Skip if both endpoints resolve to the same thing — t.ex. när
      // hela blockparet ligger inuti samma collapsed group.
      if (fromRep.id === toRep.id) continue;

      if (seenIds.has(conn.id)) continue;
      seenIds.add(conn.id);

      mapped.push({
        ...conn,
        fromId: fromRep.id,
        toId: toRep.id,
      });
    }

    return mapped;
  }, [connectors, getVisibleRepresentative]);

  // Breadcrumb data
  const breadcrumbs = useMemo(() => {
    const crumbs: Array<{ id: string | null; name: string }> = [
      { id: null, name: 'Root' },
    ];
    for (const groupId of layerPath) {
      const group = groups.find(g => g.id === groupId);
      crumbs.push({ id: groupId, name: group?.name || 'Group' });
    }
    return crumbs;
  }, [layerPath, groups]);

  return {
    layerPath,
    currentGroupId,
    currentLayerGroups,
    collapsedGroups,
    expandedGroups,
    visibleBlocks,
    visibleConnectors,
    breadcrumbs,
    navigateInto,
    navigateUp,
    navigateToLevel,
    resetLayerPath,
  };
}

/**
 * Compute the center of a group's blocks (for collapsed card default position).
 */
export function computeGroupCenter(
  group: BlockGroup,
  blocks: WorkflowBlock[]
): { x: number; y: number } {
  const groupBlocks = blocks.filter(b => group.blockIds.includes(b.id));
  if (groupBlocks.length === 0) return { x: 0, y: 0 };
  const avgX = groupBlocks.reduce((sum, b) => sum + b.x, 0) / groupBlocks.length;
  const avgY = groupBlocks.reduce((sum, b) => sum + b.y, 0) / groupBlocks.length;
  return { x: Math.round(avgX / 20) * 20, y: Math.round(avgY / 20) * 20 };
}
